import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { FlaskConical } from "lucide-react";

// PATCH 36 Me17 — sandbox demo-data mode for the admin console. When ON, the console renders a
// clearly-labelled SYNTHETIC dataset (below) that is generated client-side and NEVER read from or
// written to real customer collections — so sample data can never be confused with, or mixed into,
// real records. The per-admin flag is persisted server-side purely so the choice survives reloads.
const DemoCtx = createContext({ demo: false, setDemo: () => {}, loaded: false });

export function DemoProvider({ children, isAdmin }) {
  const [demo, setDemoState] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isAdmin) { setLoaded(true); return; }
    api.get("/polish/demo-mode").then((r) => setDemoState(!!r.data.enabled)).catch(() => {}).finally(() => setLoaded(true));
  }, [isAdmin]);

  const setDemo = useCallback((val) => {
    setDemoState(val);
    api.post("/polish/demo-mode", { enabled: val }).catch(() => {});
  }, []);

  return <DemoCtx.Provider value={{ demo, setDemo, loaded }}>{children}</DemoCtx.Provider>;
}

export const useDemo = () => useContext(DemoCtx);

// ---- Clearly-labelled synthetic sample data. Every id is prefixed SAMPLE- so it is obvious. ----
export const DEMO = {
  ccHome: {
    tiles: [
      { label: "SAMPLE · Blended margin", value: "68%", status: "ok", drill_to: "#" },
      { label: "SAMPLE · Verification backlog", value: "24", status: "warn", drill_to: "#" },
      { label: "SAMPLE · Fleet health", value: "OK", status: "ok", drill_to: "#" },
      { label: "SAMPLE · Open alerts", value: "2", status: "warn", drill_to: "#" },
    ],
    open_alerts: [
      { id: "SAMPLE-a1", type: "verification_backlog", severity: "Medium", message: "Sample: 24 connectors awaiting access-route verification." },
      { id: "SAMPLE-a2", type: "margin_watch", severity: "Low", message: "Sample: blended margin within floor, trending flat." },
    ],
    note: "SAMPLE DATA — illustrative only. No real customer records are shown.",
  },
  issues: {
    note: "SAMPLE DATA — illustrative only.",
    issues: [
      { severity: "Medium", subsystem: "Catalogue", ref: "SAMPLE-K1207 access unverified", owner: "Verification", drill_to: "#" },
      { severity: "Low", subsystem: "Billing", ref: "SAMPLE reconciliation drift £0.00", owner: "Finance", drill_to: "#" },
    ],
  },
  verifyQueue: {
    unverified_total: 24,
    note: "SAMPLE DATA — illustrative connectors only.",
    priority: [
      { id: "SAMPLE-K1207", name: "Sample Open Banking aggregator", access_class: "C" },
      { id: "SAMPLE-K0450", name: "Sample KYC provider", access_class: "C" },
      { id: "SAMPLE-K0088", name: "Sample e-signature", access_class: "B" },
    ],
  },
};

// A small, always-visible banner so no one mistakes demo mode for the live console.
export function DemoBanner() {
  const { demo, setDemo } = useDemo();
  if (!demo) return null;
  return (
    <div data-testid="demo-mode-banner" className="k-container k-no-print"
      style={{ padding: "10px 14px", marginBottom: 14, borderLeft: "3px solid var(--accent-secondary)",
        display: "flex", alignItems: "center", gap: 10 }}>
      <FlaskConical size={15} aria-hidden="true" />
      <span className="k-heading" style={{ fontSize: 13, fontWeight: 700 }}>Sample data mode</span>
      <span className="k-muted" style={{ fontSize: 12 }}>
        You're viewing illustrative sample data — no real customer records are shown or changed.
      </span>
      <button className="k-btn k-btn-ghost" data-testid="demo-mode-exit" style={{ marginLeft: "auto", padding: "6px 12px", fontSize: 12 }}
        onClick={() => setDemo(false)}>Exit sample mode</button>
    </div>
  );
}
